import React from 'react';

import { Flowchart } from '../../content/Flowchart/Flowchart';

import type { RawTextareaProps } from './RawTextarea';
import { RawTextarea } from './RawTextarea';
import { TextareaFenceUtil } from './textarea-fence-util';
import { TextareaMarkdownIndentUtil } from './textarea-markdown-indent-util';

export type TextareaFence = {
  /** A fence this says no to stays where it is, as the code block it reads as. */
  canDraw?: (code: string) => boolean;
  /**
   * `name` is the textarea's own, suffixed by where this fence sits in the answer, so no two
   * drawings on a page are named alike. `index` counts only the fences before it.
   */
  render: (fence: {
    code: string;
    index: number;
    name: string;
  }) => React.ReactNode;
};

/*
  Drawn wherever markdown is read, so an app needs no wiring to get them. `mermaid` is the tag the
  model reaches for unprompted and `flowchart` is our own name for the same thing.
*/
const FLOWCHART: TextareaFence = {
  render: ({ code, name }) => <Flowchart code={code} name={name} />,
};

const BUILT_IN: Record<string, TextareaFence> = {
  flowchart: FLOWCHART,
  mermaid: FLOWCHART,
};

export type ReadonlyTextareaProps = {
  className?: string;
  /** Drawn on top of the built-in fences, by language; an entry here replaces one of those. */
  fences?: Record<string, TextareaFence>;
  formatting?: RawTextareaProps['formatting'];
  name?: string;
  value?: string | null;
};

/* The importer counts four spaces to a nesting level; markdown is written with two. */
const reindented = (text: string | null | undefined) =>
  text ? TextareaMarkdownIndentUtil.reindentLists({ text }) : text;

export const ReadonlyTextarea = ({
  className,
  fences,
  formatting,
  name,
  value,
}: ReadonlyTextareaProps) => {
  const testName = name || 'READONLY-TEXT';
  const fenceFor = (language: string): TextareaFence | undefined =>
    fences?.[language] ?? BUILT_IN[language];

  const segments = TextareaFenceUtil.split({
    isDrawn: ({ code, language }) => {
      const fence = fenceFor(language);

      if (!fence) return false;

      /* No body to judge at the open, so the language alone decides. */
      return !code || (fence.canDraw?.(code) ?? true);
    },
    text: value ?? '',
  });

  /* Markdown with nothing drawn in it is the box it always was, down to the DOM it renders. */
  if (segments.length <= 1 && segments[0]?.type !== 'fence') {
    return (
      <RawTextarea
        className={className}
        formatting={formatting}
        name={testName}
        value={reindented(segments[0]?.text ?? value)}
        readonly
        stretched
      />
    );
  }

  /* One box per segment and no box around them: a caller reads the drawn answer back by walking
     these against the segments it split for itself. */
  return (
    <>
      {segments.map((segment, segmentIdx) => {
        /*
          Named by position in the whole answer, so a run of prose and a drawing cannot take the
          same name. `index` counts only its own kind, which is what a caller numbers its own by.
        */
        const segmentName = `${testName}_${segmentIdx}`;
        const index = segments
          .slice(0, segmentIdx)
          .filter(earlier => earlier.type === segment.type).length;

        return (
          <div key={segment.key}>
            {segment.type === 'fence' ? (
              <div
                className={`textarea-fence textarea-fence-${segment.language}`}
              >
                {fenceFor(segment.language)?.render({
                  code: segment.code,
                  index,
                  name: segmentName,
                })}
              </div>
            ) : (
              <RawTextarea
                className={className}
                formatting={formatting}
                name={segmentName}
                value={reindented(segment.text)}
                readonly
                stretched
              />
            )}
          </div>
        );
      })}
    </>
  );
};
